import { useState, useEffect } from "react";
import axios from "axios";
import { Trophy, TrendingUp, Crown, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [currentRankings, setCurrentRankings] = useState([]);
  const [historicalRankings, setHistoricalRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    try {
      setLoading(true);
      const [current, historical] = await Promise.all([
        axios.get(`${API}/rankings/current`),
        axios.get(`${API}/rankings/historical`)
      ]);
      setCurrentRankings(current.data);
      setHistoricalRankings(historical.data);
    } catch (error) {
      console.error("Error fetching rankings:", error);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeClass = (badge) => {
    const badgeMap = {
      "Lectora": "badge-lectora",
      "Activa": "badge-activa",
      "Influencer": "badge-influencer",
      "Leyenda Viva": "badge-leyenda"
    };
    return `badge ${badgeMap[badge] || 'badge-lectora'}`;
  };

  const getRankClass = (rank) => {
    if (rank === 1) return "top-1";
    if (rank === 2) return "top-2";
    if (rank === 3) return "top-3";
    return "";
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Trophy className="w-5 h-5 text-slate-300" />;
    if (rank === 3) return <Trophy className="w-5 h-5 text-orange-400" />;
    return null;
  };

  const RankingList = ({ rankings, showHistorical = false }) => (
    <div className="ranking-list">
      {rankings.length === 0 && !loading && (
        <div className="text-center text-slate-400 py-8">
          No hay datos de ranking todavía
        </div>
      )}
      {rankings.map((user) => (
        <div
          key={`${user.user_id}-${showHistorical ? 'hist' : 'curr'}`}
          className={`ranking-item ${getRankClass(user.rank)}`}
          data-testid={`ranking-item-${user.rank}`}
        >
          <div className="rank-number" data-testid={`rank-number-${user.rank}`}>
            {getRankIcon(user.rank) || `#${user.rank}`}
          </div>
          <div className="user-info">
            <div className="user-name" data-testid={`user-name-${user.rank}`}>
              {user.first_name || user.username || `Usuario ${user.user_id}`}
            </div>
            <div className="user-points" data-testid={`user-points-${user.rank}`}>
              {user.points} puntos {user.carisma_received > 0 && `💝 ${user.carisma_received} carisma`}
            </div>
          </div>
          <div className={getBadgeClass(user.badge)} data-testid={`user-badge-${user.rank}`}>
            {user.badge}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title" data-testid="page-title">
          🎮 Vandalication
        </h1>
        <p className="page-subtitle" data-testid="page-subtitle">
          Sistema de puntos y rankings del grupo
        </p>
      </div>

      <Tabs defaultValue="current" className="w-full">
        <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8 bg-slate-800/50 p-1 rounded-lg" data-testid="tabs-list">
          <TabsTrigger 
            value="current" 
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white rounded-md transition-all"
            data-testid="tab-current"
          >
            <Zap className="w-4 h-4 mr-2" />
            Mes Actual
          </TabsTrigger>
          <TabsTrigger 
            value="historical"
            className="data-[state=active]:bg-purple-500 data-[state=active]:text-white rounded-md transition-all"
            data-testid="tab-historical"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current" data-testid="current-rankings-content">
          <Card className="glass-card border-slate-700">
            <CardHeader>
              <CardTitle className="ranking-title" data-testid="current-ranking-title">
                <Trophy className="w-6 h-6 text-purple-400" />
                Top 20 - Mes Actual
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-400" data-testid="loading-indicator">
                  Cargando rankings...
                </div>
              ) : (
                <RankingList rankings={currentRankings} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historical" data-testid="historical-rankings-content">
          <Card className="glass-card border-slate-700">
            <CardHeader>
              <CardTitle className="ranking-title" data-testid="historical-ranking-title">
                <Crown className="w-6 h-6 text-yellow-400" />
                Top 20 - Histórico
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-400" data-testid="loading-indicator-historical">
                  Cargando rankings...
                </div>
              ) : (
                <RankingList rankings={historicalRankings} showHistorical />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="glass-card mt-8" data-testid="info-card">
        <h3 className="text-xl font-semibold mb-4 text-purple-300">📊 Sistema de Puntos</h3>
        <div className="grid md:grid-cols-2 gap-4 text-slate-300">
          <div>
            <h4 className="font-semibold text-purple-200 mb-2">Ganar Puntos:</h4>
            <ul className="space-y-1 text-sm">
              <li>• 1 punto por mensaje enviado</li>
              <li>• 1 punto por reacción</li>
              <li>• 1 punto por participar en encuestas</li>
              <li>• 20 puntos de carisma mensuales para regalar</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-purple-200 mb-2">Categorías (Badges):</h4>
            <ul className="space-y-1 text-sm">
              <li>• 0-50 pts: Lectora 📚</li>
              <li>• 51-150 pts: Activa ⚡</li>
              <li>• 151-300 pts: Influencer 🌟</li>
              <li>• 301+ pts: Leyenda Viva 👑</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="text-center mt-8 flex flex-wrap justify-center gap-4">
        <a 
          href="/houses" 
          className="btn-primary inline-block"
          data-testid="houses-link"
        >
          🏠 Casas del Horoscopo
        </a>
        <a 
          href="/admin" 
          className="btn-primary inline-block"
          data-testid="admin-panel-link"
        >
          Panel de Administracion
        </a>
      </div>
    </div>
  );
};

export default Dashboard;
